import { useEffect, useRef, useCallback } from "react";
import { api } from "../api";

const RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10000];

export function useUserThreadStatus(onStatus) {
  const wsRef = useRef(null);
  const callbackRef = useRef(onStatus);
  callbackRef.current = onStatus;

  // Pending reconnect timer + attempt counter — tracked in refs so the effect
  // cleanup can cancel any scheduled retry instead of leaving a zombie loop.
  const reconnectTimerRef = useRef(null);
  const attemptsRef = useRef(0);
  const stoppedRef = useRef(false);
  const stableTimerRef = useRef(null);

  const connect = useCallback(() => {
    if (stoppedRef.current) return;
    const ws = new WebSocket(api.userStatusUrl());
    wsRef.current = ws;
    ws.onopen = () => {
      // Reset backoff only after the socket stays open a while, so a server
      // that closes right after accepting doesn't cause a 1s reconnect loop.
      if (stableTimerRef.current) clearTimeout(stableTimerRef.current);
      stableTimerRef.current = setTimeout(() => { attemptsRef.current = 0; }, 10000);
    };
    ws.onmessage = (msg) => {
      let env;
      try { env = JSON.parse(msg.data); } catch (_) { return; }
      if (env.type === "thread.status" && callbackRef.current) {
        callbackRef.current(env);
      }
    };
    ws.onclose = () => {
      wsRef.current = null;
      if (stableTimerRef.current) { clearTimeout(stableTimerRef.current); stableTimerRef.current = null; }
      if (stoppedRef.current) return;
      const idx = Math.min(attemptsRef.current, RECONNECT_DELAYS_MS.length - 1);
      const delay = RECONNECT_DELAYS_MS[idx] + Math.floor(Math.random() * 500);
      attemptsRef.current += 1;
      reconnectTimerRef.current = setTimeout(connect, delay);
    };
    ws.onerror = () => { try { ws.close(); } catch (_) {} };
  }, []);

  useEffect(() => {
    stoppedRef.current = false;
    attemptsRef.current = 0;
    connect();
    return () => {
      // Stop the reconnect loop on unmount: clear any pending retry timer
      // AND prevent in-flight close handlers from scheduling new ones.
      stoppedRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (stableTimerRef.current) {
        clearTimeout(stableTimerRef.current);
        stableTimerRef.current = null;
      }
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (_) {}
        wsRef.current = null;
      }
    };
  }, [connect]);
}
